/* Awaiting Tracking — Emirates parcels still on a blank / temporary EMI
 * tracking more than 7 days after dispatch. */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Download, Search, Upload } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { API, getAuthHeader } from "@/App";

const URL_ = () => `${API}/order-register/emirates-tracking/awaiting`;

export const AwaitingTrackingBadge = ({ testId, onUpload, refreshKey }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    axios.get(URL_(), { headers: getAuthHeader() }).then(r => setData(r.data)).catch(() => {});
  }, [refreshKey]);
  if (!data) return null;
  const rows = data.rows.filter(r => !q.trim() ||
    `${r.customer} ${r.code} ${r.invoice_no} ${r.tracking}`.toLowerCase().includes(q.trim().toLowerCase()));
  const excel = async () => {
    try {
      const r = await axios.get(`${URL_()}?format=xlsx`, { headers: getAuthHeader(), responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "Emirates_Awaiting_Tracking.xlsx";
      a.click();
    } catch { toast.error("Excel download failed"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
              className={`h-8 text-[11px] font-semibold ${data.count ? "border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100" : "border-slate-300 text-slate-500"}`}
              title="Emirates parcels still waiting for a real tracking more than 7 days after dispatch"
              data-testid={`awaiting-tracking-btn-${testId}`}>
        <Clock className="w-3.5 h-3.5 mr-1" />Awaiting Tracking
        <span className={`ml-1 rounded-full px-1.5 text-[10px] leading-4 ${data.over_15 ? "bg-rose-600 text-white" : data.count ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-600"}`}
              data-testid={`awaiting-tracking-count-${testId}`}>{data.count}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[1100px] max-h-[90vh] overflow-y-auto" data-testid="awaiting-tracking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Awaiting Emirates Tracking</DialogTitle>
            <DialogDescription className="text-xs">
              {data.count} parcels on a blank / temporary EMI tracking for more than 7 days after dispatch
              {data.over_15 ? ` · ${data.over_15} over 15 days (red)` : ""}. Dispatch = Database A date, else invoice completed date.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-60">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / code / invoice"
                     className="h-7 pl-7 text-xs" data-testid="awaiting-tracking-search" />
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={excel} data-testid="awaiting-tracking-excel">
              <Download className="w-3 h-3 mr-1" />Excel
            </Button>
            <Button size="sm" className="h-7 text-xs ml-auto bg-sky-600 hover:bg-sky-700 text-white"
                    onClick={() => { setOpen(false); onUpload(); }} data-testid="awaiting-tracking-upload">
              <Upload className="w-3 h-3 mr-1" />Upload Emirates trackings
            </Button>
          </div>
          <div className="overflow-auto max-h-[60vh] border rounded-lg">
            <table className="w-full text-left text-[11px]" data-testid="awaiting-tracking-table">
              <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase text-slate-500">
                <tr>{["Customer", "Code", "Invoice", "Order", "Dispatch", "Days", "Current tracking", "Address"]
                  .map(h => <th key={h} className="px-2 py-1.5 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={`border-b ${r.days > 15 ? "bg-rose-50 text-rose-800" : ""}`} data-testid={`awaiting-row-${i}`}>
                    <td className="px-2 py-1 font-medium">{r.customer}</td>
                    <td className="px-2 py-1">{r.code}</td>
                    <td className="px-2 py-1">{r.invoice_no}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{r.order_date}</td>
                    <td className="px-2 py-1 whitespace-nowrap" title={r.basis}>{r.dispatch_date}</td>
                    <td className="px-2 py-1 font-bold">{r.days}</td>
                    <td className="px-2 py-1 font-mono">{r.tracking || "—"}</td>
                    <td className="px-2 py-1 text-slate-500 max-w-[280px] truncate" title={r.address}>{r.address}</td>
                  </tr>))}
              </tbody>
            </table>
            {!rows.length && <p className="text-center text-xs text-slate-400 py-6" data-testid="awaiting-empty">All dispatched Emirates parcels have a real tracking</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
